/**
 * useSEO — Vue 3 composable for per-route head management.
 * Dynamically sets document title, meta tags, Open Graph tags,
 * Twitter Card tags, canonical URL, robots directives, and hreflang.
 *
 * No external dependencies — uses only the DOM API.
 */

const CANONICAL_BASE = 'https://wordcloudstudio.com'

/**
 * Set or replace a <meta> element by name/property selector.
 */
function setMeta(name, content, isProperty = false) {
  if (!content) return
  const attr = isProperty ? 'property' : 'name'
  let el = document.querySelector(`meta[${attr}="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/**
 * Remove a <meta> element by name/property.
 */
function removeMeta(name, isProperty = false) {
  const attr = isProperty ? 'property' : 'name'
  const el = document.querySelector(`meta[${attr}="${name}"]`)
  if (el) el.remove()
}

/**
 * Set or replace a <link> element by rel.
 */
function setLink(rel, href) {
  if (!href) return
  let el = document.querySelector(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Set or replace a <link rel="alternate" hreflang="..."> element.
 */
function setHreflang(lang, href) {
  if (!href) return
  let el = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'alternate')
    el.setAttribute('hreflang', lang)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Core composable. Call in the mounted / activated hook of each page component.
 *
 * @param {Object} options
 * @param {string} options.title          — Document title
 * @param {string} options.description    — Meta description
 * @param {string} options.keywords       — Meta keywords (comma-separated)
 * @param {string} options.canonical      — Canonical URL path (e.g. '/price')
 * @param {boolean} options.noindex       — Add robots noindex
 * @param {string} options.ogTitle        — og:title (defaults to title)
 * @param {string} options.ogDescription  — og:description (defaults to description)
 * @param {string} options.ogImage        — og:image URL
 * @param {string} options.ogType         — og:type (default: 'website')
 * @param {string} options.twitterTitle   — twitter:title
 * @param {string} options.twitterDescription — twitter:description
 * @param {string} options.twitterImage   — twitter:image
 */
export function useSEO({
  title = '',
  description = '',
  keywords = '',
  canonical = '',
  noindex = false,
  ogTitle = '',
  ogDescription = '',
  ogImage = '',
  ogType = 'website',
  twitterTitle = '',
  twitterDescription = '',
  twitterImage = '',
} = {}) {
  // ── Title ──────────────────────────────────────────────
  if (title) {
    document.title = title
    setMeta('twitter:title', twitterTitle || title)
    setMeta('og:title', ogTitle || title, true)
  }

  // ── Description ────────────────────────────────────────
  if (description) {
    setMeta('description', description)
    setMeta('og:description', ogDescription || description, true)
    setMeta('twitter:description', twitterDescription || description)
  }

  // ── Keywords ───────────────────────────────────────────
  if (keywords) {
    setMeta('keywords', keywords)
  }

  // ── Canonical ──────────────────────────────────────────
  setLink('canonical', canonical ? `${CANONICAL_BASE}${canonical}` : CANONICAL_BASE)

  // ── Robots ─────────────────────────────────────────────
  if (noindex) {
    setMeta('robots', 'noindex, nofollow')
  } else {
    removeMeta('robots')
  }

  // ── Open Graph ─────────────────────────────────────────
  if (ogImage) {
    setMeta('og:image', ogImage, true)
  }
  if (ogType) {
    setMeta('og:type', ogType, true)
  }

  // ── Twitter Card ───────────────────────────────────────
  if (twitterImage) {
    setMeta('twitter:image', twitterImage)
  }

  // ── Hreflang (default to x-default and en) ─────────────
  if (canonical !== undefined) {
    const href = canonical ? `${CANONICAL_BASE}${canonical}` : CANONICAL_BASE
    setHreflang('x-default', href)
    setHreflang('en', href)
  }
}

export default useSEO
